import {filter, fromEntries, PathsWithTarget, pipe, toEntries} from '@logistics-frontend/utils'

import {useTypedField} from '../hooks'

// TODO Открытые вопросы:
// - что делать с radio, у которого value не совпадает с состоянием "включенности" (поресечить что с этим делает RHF)
// (может и ничего)
// - при использовании могут возникнуть дополнительные проблемы, это нормально и нужно приходить к @jeaciaz их решать по
// мере поступления чтобы довести концепт до удобного состояния

type SyntheticProps<TValue> = {
  onChange: (newValue: TValue) => void
  onBlur: VoidFunction
  error?: string
  controlRef: React.Ref<HTMLElement>
}

export type ScopedComponentOptions<TValueKey extends string> = {
  /**
   * Название пропса, который содержит значение, хранящееся в форме.
   * @default 'value'
   */
  valueKey?: TValueKey
  /**
   * Нужно ли делать компонент touched сразу после изменения в целях валидации
   * @default true
   **/
  touchOnChange?: boolean
  /**
   * Перечисленные пропсы не будут передаваться во вложенный компонент. Обычно стоит указывать поля, которые могут по
   * цепочке передаться до нативного элемента, но не поддерживаются им.
   */
  ignoreFields?: Array<keyof SyntheticProps<unknown>>
}

/**
 * Преобразует controlled компонент с value/onChange в компонент формы с пропсом `name`. После создания компоненту нужно
 * указать тип данных формы, в рамках которой он работает, после чего `name` будет тайпчекаться.
 *
 * Пример использования:
 * ```ts
 * export const OrderInput = scoped(Input)<Order>
 * // ...
 * <OrderInput name="..." />
 *
 * // or
 *
 * export const ScopedInput = scoped(Input)
 * // ...
 * <ScopedInput<Order> name="..." />
 * // В случае если value определяется по месту использования:
 * <ScopedSelect<Order, number> name="..." />
 * ```
 */
export const scoped =
  <TComponent extends React.ElementType, TValueKey extends string = 'value'>(
    Component: TComponent,
    options: ScopedComponentOptions<TValueKey> = {},
  ) =>
  <
    TForm = never,
    TValue = React.ComponentPropsWithoutRef<TComponent> extends {[K in TValueKey]?: unknown}
      ? React.ComponentPropsWithoutRef<TComponent>[TValueKey]
      : never,
    TProps = React.ComponentPropsWithoutRef<TComponent>,
  >({
    name,
    ...props
  }: Omit<TProps, TValueKey | 'name' | 'onChange' | 'onBlur'> & {
    name: TForm extends never ? never : PathsWithTarget<TForm, TValue>
  } & {
    onChange?: (val: TValue) => void
    onBlur?: VoidFunction
  }) => {
    const {valueKey = 'value', touchOnChange = true, ignoreFields = []} = options
    const [value, _onChange, {onBlur: _onBlur, ref: controlRef, error}] = useTypedField<TForm, TValue>(name)

    const onBlur = () => {
      props.onBlur?.()
      _onBlur()
    }

    const onChange = (val: TValue) => {
      props.onChange?.(val)
      _onChange(val)

      if (touchOnChange) {
        onBlur()
      }
    }

    const passedProps: Partial<SyntheticProps<TValue>> = pipe(
      {
        onBlur,
        onChange,
        controlRef,
        error,
      },
      toEntries,
      filter(([key]) => !ignoreFields.includes(key as keyof SyntheticProps<TValue>)),
      fromEntries,
    )

    // @ts-expect-error имеет место комбинаторный взрыв тайпскрипта
    return <Component {...props} {...{[valueKey]: value, ...passedProps}} />
  }

/**
 * Тип-утилита для миграции сложных компонентов. Берет компонент, у которого name - string, и переназначает ему тип на
 * более строгий
 *
 * Без необходимости лучше его не использовать, он не особо удобный и нужен чисто для миграции
 */
export type Scoped<TComponent extends React.ElementType, TValue> = TComponent extends React.ElementType<infer TProps>
  ? <TForm>(props: Omit<TProps, 'name'> & {name: PathsWithTarget<TForm, TValue>}) => React.ReactElement
  : never

/**
 * Используется вместе с `useTypedField` для создания каких-то кастомных контролов в рамках формы. Пример:
 *
 * ```ts
 * type OrderInputProps = FormControlProps<Order, string, {
 *   label?: string
 *   // ...
 * }>
 * // ...
 * function OrderInput({name, ...props}: OrderInputProps) {
 *   const [value, onChange, {onBlur, ref}] = useTypedField<Order, string>(name)
 *   return <input {...props} value={value} onChange={onChange} onBlur={onBlur} ref={ref} />
 * }
 * ```
 */
export type FormControlProps<TForm, TValue, TProps> = TProps & {
  name: PathsWithTarget<TForm, TValue>
}
